/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import * as Yup from 'yup';
import PrimaryButton from '../../../Forms/PrimaryButton';
import { getRequiredLabel } from '../../../Util/getRequiredLabel';

const route = 'webinars';
const basePath = '/webinars-feature';

const rootCss = css`
    max-width: 100%;

    .form-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        .save-btn-container {
            display: flex;
            margin-top: 100px !important;
        }
    }

    .cke_notification {
        display: none;
    }

    .preview-image {
        margin-bottom: 20px;
    }

    .preview-upload-help {
        margin: 0px 0 30px 0;
        font-size: 14px;
        color: var(--Z8);
    }
`;

const helpContainerCss = css`
    display: flex;
    align-items: end;
    gap: 6px;
    width: 100%;
    margin-bottom: 10px;
`;

const requiredIfPublished = (baseSchema, message) =>
  baseSchema.when('publishStatus', {
    is: 'Published',
    then: schema => schema.required(message),
    otherwise: schema => schema.notRequired(),
  });

const publishedRequiredText = 'All fields are required to save a published webinar';
const draftRequiredText = 'The folowing fields are required to save a draft: Title, Preview Title, DateTime, DateTime Offset, Room ID, Presenter First & Last Name';

const validationSchema = Yup.object().shape({
    webinarId: Yup.number().required('Webinar Id is required'),
    publishStatus: Yup.string().required('Status is required'),
    platformId: Yup.number().required('Platform is required'),
    title: Yup.string().required('Title is required').max(100, 'Title cannot exceed 100 characters'),
    previewTitle: Yup.string().required('Preview Title is required').max(50, 'Preview Title cannot exceed 50 characters'),
    roomId: Yup.string().required('Room ID is required'),
    webinarDateTime: Yup.date().required('Webinar DateTime is required'),
    dateTimeOffset: Yup.string().required('DateTime Offset is required'),
    presenterName: Yup.string().required('Presenter first name is required'),
    presenterNameLast: Yup.string().required('Presenter last name is required'),

    presenterTitle: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    presenterHeadshotUrl: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    cardBannerUrl: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    cardBannerAltText: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    qnsId1: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    qnsId2: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),
    hasOpenEndedQuestion: Yup.boolean(),
    openEndedQnsId: Yup.string().nullable().when('hasOpenEndedQuestion', {
        is: true,
        then: schema => schema.required('Select an open-ended question'),
    }),
    aboutText: requiredIfPublished(Yup.string()
        .nullable().max(2000, 'About text cannot exceed 2000 characters'), 'About text is required'),
    presenterAbout: requiredIfPublished(Yup.string().nullable(), publishedRequiredText),

    onForAllCobrands: Yup.boolean().required('This field is required'),
    onForAllCountries: Yup.boolean().required('This field is required'),
    cobrands: Yup.array()
            .of(Yup.number().required('Cobrand ID is required'))
            .nullable(),
    countries: Yup.array()
            .of(Yup.string().required('Country is required'))
            .nullable()
})
.test(
    'required-for-draft',
    draftRequiredText,
    function (values) {
        const { 
            publishStatus, platformId, title, previewTitle, roomId, webinarDateTime, dateTimeOffset, presenterName, presenterNameLast 
        } = values || {};

        if (publishStatus === 'draft') {
            return Boolean(platformId && title && previewTitle && roomId && webinarDateTime && dateTimeOffset && presenterName && presenterNameLast);
        }
        return true;
    }
);

function mapInitialValues(data) {
    return data && Object.keys(data).length 
            ? { ...data,
                // WebinarDateTime is wall-clock time in dateTimeOffset's zone; drop the UTC marker the API serializer adds
                webinarDateTime: data.webinarDateTime ? data.webinarDateTime.replace(/(Z|[+-]\d{2}:?\d{2})$/, '') : '',
                presenterHeadshotUrl: data.presenterHeadshotUrl || '',
                qnsId1: data.qnsId1 ? data.qnsId1.toString() : '',
                qnsId2: data.qnsId2 ? data.qnsId2.toString() : '',
                openEndedQnsId: data.openEndedQnsId ? data.openEndedQnsId.toString() : '',
             } 
            : { 
                webinarId: 0, 
                publishStatus: 'Draft',
                publishStatusId: 1, 
                platformId: 1, 
                title: '',
                previewTitle: '',
                roomId: '',
                webinarDateTime: '',
                dateTimeOffset: '',
                presenterName: '',
                presenterNameLast: '',
                presenterTitle: '',
                presenterHeadshotUrl: '',
                cardBannerUrl: '',
                cardBannerAltText: '',
                cardImageURL:'',
                qnsId1: null,
                qnsId2: null,
                hasOpenEndedQuestion: false,
                openEndedQnsId: '',
                aboutText: '',
                presenterAbout: '',
                onForAllCobrands: true,
                onForAllCountries: true,
                cobrands: [],
                countries: [],
             };
}

const successMsg = 'Webinars Updated.';

const setErrorMsg = (show, errors) => (
    show && Object.keys(errors).length > 0
        ? `Errors in: ${Object.keys(errors).join(', ')}. Check all required * fields.`
        : 'Errors in form, check all required fields and try again.'
);

const setStatusHelpMsg = (status) => (
    status === 'Published' ? publishedRequiredText : draftRequiredText
);

const statusTextMap = {
  1: 'Draft',
  2: 'Published',
};

const statusOptions = [
    { key: 1, text: 'Draft' },
    { key: 2, text: 'Published' },
];

const platformOptions = [
    { key: 1, text: 'iGrad' },
    { key: 2, text: 'Enrich' },
];

const timezoneOptions = [
    { key: 'Pacific Standard Time', text: 'America/Los_Angeles (-08:00)' },
    { key: 'Mountain Standard Time', text: 'America/Denver (-07:00)' },
    { key: 'Central Standard Time', text: 'America/Chicago (-06:00)' },
    { key: 'Eastern Standard Time', text: 'America/Toronto (-05:00)' },
];

const basicInfoFields = [
    { name: 'title', label: 'Webinar Title', required: true },
    { name: 'previewTitle', label: 'Webinar Preview Title', required: true },
    { name: 'roomId', label: 'Room ID', required: true },
];

const presenterInfoFields = (isPublished) => [
    { name: 'presenterName', label: 'Presenter First Name', required: true },
    { name: 'presenterNameLast', label: 'Presenter Last Name', required: true },
    { name: 'presenterTitle', label: 'Presenter Title', required: isPublished },
];

const onForAllCobrandsOptions = [
    { key: true, text: 'True' },
    { key: false, text: 'False' },
];

const onForAllCountriesOptions = [
    { key: true, text: 'True' },
    { key: false, text: 'False' },
];

const localizationOptions = [
    { key: true, text: 'Global' },
    { key: false, text: 'Not Global' },
];

const bannerSizes = [
      { path: 'IMAGE/Redesign/Banners/og/', width: undefined, height: undefined },
      { path: 'IMAGE/Redesign/Banners/sm/', width: 768, height: 188 },
      { path: 'IMAGE/Redesign/Banners/md/', width: 992, height: 243 },
      { path: 'IMAGE/Redesign/Banners/lg/', width: 1900, height: 465 },
    ];

const unsavedMsg = 'You have unsaved changes, are you sure you want to leave?';
const bannerHelpMsg = 'Photo must be - 1800px width x 440px height. Make sure the most important content of the image is in the center, and that content on the image is spread out overall, as the size / shape of the image will adjust to different screen sizes.';

const resolveMediaUrl = (inputUrl, path) => {
    const url = inputUrl || '';
    const isFullUrl = url.startsWith('http://') || url.startsWith('https://');
    return isFullUrl ? url : url !== '' ? `https://media-cdn.igrad.com/${path}${url}` : null;
};

const SaveButton = ({ handleSave, dirty, submitting }) => {
    return (
        <div className='save-btn-container'>
            <PrimaryButton
                onClick={handleSave}
                text='Save'
                disabled={!dirty || submitting}
            />
        </div>
    );
};

const getHeadshotOptions = (values) => ({
    name: 'presenterHeadshotUrl',
    label: 'Presenter Headshot URL',
    helpMsg: 'Upload a square 300px by 300px headshot',
    showHelpIcon: true,
    previewUrl: resolveMediaUrl(values.presenterHeadshotUrl, 'IMAGE/Webinar-Headshots/'),
    width: values.presenterHeadshotUrl === '' ? 'auto' : 300,
    height: values.presenterHeadshotUrl === '' ? 'auto' : 300,
    required: values.publishStatus === 'Published',
    gallery: {
        name: 'presenterHeadshotUrl',
        blobUrl: 'https://igradcdnproduction.blob.core.windows.net',
        containerName: 'media',
        folderName: 'IMAGE/Webinar-Headshots/',
        imageUrl: 'https://media-cdn.igrad.com/',
        heading: 'Select a headshot or upload a new one',
        btnText: 'Select Headshot'
    },
});

const getBannerOptions = (values) => ({
    name: 'cardBannerUrl',
    label: 'Large Banner',
    helpMsg: bannerHelpMsg,
    showHelpIcon: true,
    previewUrl: resolveMediaUrl(values.cardBannerUrl, 'IMAGE/Redesign/Banners/lg/'),
    width: 'auto',
    height: 'auto',
    required: values.publishStatus === 'Published',
    gallery: {
        name: 'cardBannerUrl',
        blobUrl: 'https://igradcdnproduction.blob.core.windows.net',
        containerName: 'media',
        folderName: 'IMAGE/Redesign/Banners/lg/',
        imageUrl: 'https://media-cdn.igrad.com/',
        heading: 'Select a banner or upload a new one',
        btnText: 'Select Banner',
        sizePathGroup: bannerSizes,
    },
});

export {
    route,
    basePath,
    rootCss,
    helpContainerCss,
    validationSchema,
    mapInitialValues,
    successMsg,
    setErrorMsg,
    setStatusHelpMsg,
    statusTextMap,
    statusOptions,
    platformOptions,
    timezoneOptions,
    basicInfoFields,
    presenterInfoFields,
    onForAllCobrandsOptions,
    onForAllCountriesOptions,
    localizationOptions,
    unsavedMsg,
    SaveButton,
    getBannerOptions,
    getHeadshotOptions,
};
